import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Lightbulb, Users, Timer, ShieldCheck, ArrowRight, Calendar } from "lucide-react";
import { BRAND, CTA_POINTS } from "../lib/constants";

const ICON_MAP = { Lightbulb, Users, Timer, ShieldCheck };

export default function CTA() {
  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="relative theme-section py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-35 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/50 to-transparent" />
      <div className="absolute -top-24 right-1/4 w-[420px] h-[420px] bg-[#00C2FF] rounded-full blur-[180px] opacity-10" />
      <div className="absolute -bottom-32 left-1/4 w-[380px] h-[380px] bg-[#003E8C] rounded-full blur-[200px] opacity-35" />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12 sm:mb-16"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-[#00C2FF]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
                / Get Started
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tighter">
              Ready to{" "}
              <span className="text-gradient-cyan italic">modernize</span>
              <br />
              your trading infrastructure?
            </h2>
          </div>

          <p className="text-gray-400 text-base leading-relaxed max-w-md lg:text-right lg:pb-1">
            Schedule a 30-minute scoping session. We&apos;ll review your current stack and outline
            where {BRAND.name} fits.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative rounded-3xl overflow-hidden"
        >
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#00C2FF]/25 via-[#003E8C]/40 to-[#9FE3FF]/10 p-px">
            <div className="absolute inset-[1px] rounded-[23px] theme-panel" />
          </div>

          <div className="relative grid lg:grid-cols-12 gap-0">
            <div className="lg:col-span-5 p-8 sm:p-10 lg:p-12 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-white/10 min-h-[280px]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#00C2FF]/30 bg-[#00C2FF]/10 px-4 py-2 mb-8">
                  <Calendar className="w-4 h-4 text-[#00C2FF]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9FE3FF] font-semibold">
                    30-min discovery
                  </span>
                </div>
                <p className="theme-subtle text-lg leading-relaxed">
                  Identify bottlenecks and leave with a practical integration roadmap — direct
                  technical guidance, not generic pitches.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-10">
                <Link
                  to="/contact"
                  data-testid="cta-book-demo"
                  className="btn-cyan inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold"
                >
                  <Rocket className="w-4 h-4" />
                  Book a Demo
                </Link>
                <Link
                  to="/services"
                  data-testid="cta-explore"
                  className="btn-outline inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold"
                >
                  View Services
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10">
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 h-full">
                {CTA_POINTS.map((p, i) => {
                  const Icon = ICON_MAP[p.icon] || Lightbulb;
                  return (
                    <motion.div
                      key={p.text}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.15 + i * 0.07 }}
                      data-testid={`cta-point-${i}`}
                      className="group relative flex flex-col justify-between rounded-2xl p-5 sm:p-6 bg-white/[0.03] border border-white/10 hover:border-[#00C2FF]/45 hover:bg-[#00C2FF]/[0.06] transition-all duration-300 min-h-[140px]"
                    >
                      <span className="font-mono text-[10px] text-slate-400 dark:text-white/25 tracking-widest">
                        0{i + 1}
                      </span>
                      <div className="flex items-end justify-between gap-3 mt-4">
                        <p className="theme-subtle font-medium text-sm sm:text-base leading-snug pr-2">
                          {p.text}
                        </p>
                        <div
                          className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                            i % 2 === 0
                              ? "bg-[#00C2FF]/15 border border-[#00C2FF]/40 text-[#00C2FF]"
                              : "bg-[#9FE3FF]/15 border border-[#9FE3FF]/40 text-[#9FE3FF]"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
